import { z } from "zod";

import { WidgetDto, WidgetDtoSchema } from "../../dtos/widgets";
import { RecursivePartial } from "../../utils";

import {
  MessageBoxWidgetConfig,
  MessageBoxWidgetStyles,
  MessageBoxWidgetVariables,
  MessageBoxWidgetVariant,
} from "./discount-message-box-widget";
import {
  AnyProgressBarWidgetConfig,
  AnyProgressBarWidgetStyles,
  AnyProgressBarWidgetVariables,
  AnyProgressBarWidgetVariant,
} from "./discount-progress-bar-widget";
import {
  DiscountQuantityWidgetConfig,
  DiscountQuantityWidgetStyles,
  DiscountQuantityWidgetVariables,
  DiscountQuantityWidgetVariant,
} from "./discount-quantity-widget";

export const WidgetTypeList = [
  "messageBoxPopup",
  "messageBoxSlideOut",
  "quantityButtons",
  "cartMessage",
  "discountProgressBar",
  "shippingProgressBar",
] as const;

export type WidgetTypeType = (typeof WidgetTypeList)[number];

export const WidgetConfigSchema = z.object({
  desktop: z.any().default({}),
  mobile: z.any().optional(),
});

export interface WidgetConfigType<
  TVariant extends string,
  TStyles extends WidgetStyleConfig,
  TVariables extends object,
> {
  desktop?: WidgetDeviceConfigType<TVariant, TStyles, TVariables>;
  mobile?: RecursivePartial<
    WidgetDeviceConfigType<TVariant, TStyles, TVariables>
  >;
}

export interface WidgetDeviceConfigType<
  TVariant extends string,
  TStyles extends WidgetStyleConfig,
  TVariables extends object,
> {
  variant?: TVariant;
  variables?: TVariables;
  styles?: TStyles;
}

export const WidgetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  enabledSitewide: z.boolean().optional().default(false),
  parentId: z.string().uuid().nullish(),
  widgetType: z.enum(WidgetTypeList),
  config: WidgetConfigSchema,
});

export interface WidgetLikeObject {
  name: string;
  widgetType: WidgetTypeType;
  toModel: () => WidgetDto;
  setStyles: () => void;
}

export type WidgetVariant =
  | DiscountQuantityWidgetVariant
  | AnyProgressBarWidgetVariant
  | MessageBoxWidgetVariant;

export type WidgetConfig =
  | DiscountQuantityWidgetConfig
  | AnyProgressBarWidgetConfig
  | MessageBoxWidgetConfig;

export type WidgetVariables =
  | DiscountQuantityWidgetVariables
  | AnyProgressBarWidgetVariables
  | MessageBoxWidgetVariables;

export type WidgetStyles =
  | DiscountQuantityWidgetStyles
  | AnyProgressBarWidgetStyles
  | MessageBoxWidgetStyles;

export type WidgetStyleConfig = Record<
  string,
  Record<string, string> | undefined
>;

export type WidgetPrefix =
  | "ig-mb" // discount message box widget
  | "ig-pb" // discount progress bar widget
  | "ig-dq" // discount quantity widget
  | "ig-dc" // discount cart widget
  | "ig-spb"; // shipping progress bar widget

export type WidgetPrefixes = Partial<Record<WidgetPrefix, string>>;

export * from "./discount-cart-widget";
export * from "./discount-message-box-widget";
export * from "./discount-progress-bar-widget";
export * from "./discount-quantity-widget";
export * from "./shipping-progress-bar-widget";

export interface WidgetManagerLikeObject {
  loadQuantityWidget: () => boolean;
  loadDiscountProgressBarWidget: () => boolean;
  loadShippingProgressBarWidget: () => boolean;
  loadMessageBoxWidget: () => boolean;
  updateAllWidgets: () => Promise<void>;
}
